import React, { useEffect, useMemo, useState } from 'react';

import styled from '@emotion/styled';
import { Button } from '@lightspeed/flame/Button';
import { Box } from '@lightspeed/flame/Core';
import { theme } from '@lightspeed/flame/Core/themes/flame';
import { Input } from '@lightspeed/flame/Input';
import { InputGroup } from '@lightspeed/flame/InputGroup';
import { Modal, ModalBody } from '@lightspeed/flame/Modal';
import { Text } from '@lightspeed/flame/Text';

import { createReactRenderer } from 'helpers/react-renderer';
// import useLocalizedLink from 'root/apps/hooks/useLocalizedLink';

interface CalendarFeedModalProps {
  searchValues: Record<string, any>;
}

const StyledModal = styled(Modal)`
  width: 720px;
`;

const CalendarFeedModal: React.FC<CalendarFeedModalProps> = ({ searchValues }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [calendarFeedUrl, setCalendarFeedUrl] = useState<string | null>(null);
  const [isLoadingCalendarUrl, setIsLoadingCalendarUrl] = useState(false);
  const [calendarUrlError, setCalendarUrlError] = useState<string | null>(null);
  const [copyStatusMessage, setCopyStatusMessage] = useState<boolean>(false);

  const shopID = useMemo(() => Number(searchValues.shop_id ?? -1), [searchValues.shop_id]);

  // const learnMoreUrl = useLocalizedLink(
  //   'https://retail-support.lightspeedhq.com/hc/{{locale}}/articles/TODO-workorder-calendar-feed'
  // );

  const handleCopyCalendarUrl = async () => {
    if (!calendarFeedUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(calendarFeedUrl);
      setCopyStatusMessage(true);
    } catch (error) {
      setCopyStatusMessage(false);
    }
  };

  const getCalendarFeedError = (): string | null => {
    if (searchValues.id_search) {
      return 'Calendar feeds does not support ID search. Update your filters and remove the ID search, then try again.';
    }
    if (shopID === -1) {
      return 'Calendar feeds can only be generated for one shop at a time. Update your filters to select a specific shop, then try again.';
    }
    return null;
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const customerID = Number(searchValues?.customer_id ?? -1);
    const employeeID = Number(searchValues?.employee_id ?? -1);

    const rawWorkorderStatusIDs = searchValues?.workorder_status_id;
    let normalizedWorkorderStatusIDs: unknown[] = [];

    if (rawWorkorderStatusIDs != null && typeof rawWorkorderStatusIDs === 'object') {
      normalizedWorkorderStatusIDs = Object.values(rawWorkorderStatusIDs);
    } else if (rawWorkorderStatusIDs != null && rawWorkorderStatusIDs !== '') {
      normalizedWorkorderStatusIDs = [rawWorkorderStatusIDs];
    }

    const workorderStatusIDs = normalizedWorkorderStatusIDs
      .map((statusID) => Number(statusID))
      .filter((statusID) => Number.isFinite(statusID));

    if (!workorderStatusIDs.length) {
      workorderStatusIDs.push(-1);
    }

    // Query string parameters for calendar feed URL request
    const params = new URLSearchParams();
    params.append('customerID', String(Number.isFinite(customerID) ? customerID : -1));
    params.append('employeeID', String(Number.isFinite(employeeID) ? employeeID : -1));
    params.append('shopID', String(Number.isFinite(shopID) ? shopID : -1));
    workorderStatusIDs.forEach((statusID) => {
      params.append('workorderStatusIDs[]', String(statusID));
    });

    const fetchCalendarUrl = async () => {
      setIsLoadingCalendarUrl(true);
      setCalendarUrlError(null);
      setCalendarFeedUrl(null);
      setCopyStatusMessage(false);

      try {
        const response = await fetch(`/admin/calendar/workorders/url?${params.toString()}`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        });

        const responseData = await response.json();
        const workorderCalendarFeedUrl = responseData?.data?.workorder_calendar_feed_url;

        if (!workorderCalendarFeedUrl) {
          throw new Error('Missing workorder_calendar_feed_url in response');
        }

        setCalendarFeedUrl(workorderCalendarFeedUrl);
      } catch (error) {
        setCalendarUrlError('Unable to generate calendar URL. Please try again.');
        setCalendarFeedUrl(null);
      } finally {
        setIsLoadingCalendarUrl(false);
      }
    };

    fetchCalendarUrl();
  }, [isOpen, searchValues, shopID]);

  return (
    <>
      <a
        href="#"
        title="Link Calendar"
        className="calendar griffin-no-display" // calendar class is for calendar icon styling, griffin-no-display hides the link in non-JS environments
        data-test="calendar-feed-link"
        onClick={(e) => {
          e.preventDefault();
          const error = getCalendarFeedError();
          if (error) {
            window.alertUser(error, 15);
            return;
          }
          setIsOpen(true);
        }}
      >
        Calendar Link
      </a>

      <StyledModal
        isOpen={isOpen}
        onRequestClose={() => setIsOpen(false)}
        data-test="calendar-feed-modal"
      >
        <ModalBody>
          <Text fontSize={32} fontWeight={600} mb={5} data-test="calendar-feed-title">
            Generate calendar feed
          </Text>
          <Box mb={5}>
            <Text data-test="calendar-feed-instructions-criteria">
              This link includes work orders for specific statuses and filters. Copy the URL below
              and add it to your calendar app (Google Calendar, Apple Calendar, or Outlook).
            </Text>
          </Box>
          <Box mb={5}>
            <Text data-test="calendar-feed-instructions-note">
              Note: Work orders with an{' '}
              <Text as="span" fontWeight="600">
                &ldquo;Estimate&rdquo;
              </Text>
              ,{' '}
              <Text as="span" fontWeight="600">
                &ldquo;Done & Paid&rdquo;
              </Text>
              , and{' '}
              <Text as="span" fontWeight="600">
                &ldquo;Finished&rdquo;
              </Text>{' '}
              status will not sync to your calendar.{' '}
              {/* <TextLink href={learnMoreUrl} target="_blank" rel="noreferrer">
                Learn more
              </TextLink> */}
            </Text>
          </Box>
          <Box>
            <InputGroup>
              <Input
                readOnly
                value={calendarFeedUrl ?? ''}
                placeholder={isLoadingCalendarUrl ? 'Generating URL…' : 'URL'}
                onChange={() => {}}
                className="notranslate"
                data-test="calendar-feed-url-input"
                style={{ borderRadius: '3px' }}
              />
              <Button
                ml={2}
                variant="secondary"
                fill={true}
                style={{ borderRadius: '3px', width: '90px' }}
                onClick={handleCopyCalendarUrl}
                disabled={!calendarFeedUrl || isLoadingCalendarUrl}
                data-test="calendar-feed-copy-button"
              >
                {copyStatusMessage ? 'Copied' : 'Copy'}
              </Button>
            </InputGroup>
          </Box>
          {calendarUrlError && (
            <Box mt={2}>
              <Text color={theme.colors['maple-500']} data-test="calendar-feed-error">
                {calendarUrlError}
              </Text>
            </Box>
          )}
          <Box mt={4}>
            <Text data-test="calendar-feed-instructions-filters">
              Changed your filters? Generate a new link.
            </Text>
          </Box>
        </ModalBody>
      </StyledModal>
    </>
  );
};

export default CalendarFeedModal;

const RenderedComponent = createReactRenderer({
  component: CalendarFeedModal,
  selector: '[data-react-root="WorkOrderCalendarFeed"]',
});

history.onChangeState(
  [
    {
      name: ['workbench.listings.workorders', 'workbench.listings.workorders_agenda'],
      id: undefined,
    },
  ],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
